<!--
  @component CostPanel — Phase 4 account-wide cost + billing (user-scoped).
  tier:   deployments section (scope-independent — billing rolls every project
          into one account)

  Real Stripe-backed surface: current tier + subscription, recent invoices
  (amounts + hosted links), and the account roll-up. The internal tier is $0
  (platform fee + metered), but every object — customer, subscription, invoice
  — is a real Stripe object on the same code path as future paid tiers.
  Honest when Stripe isn't configured (NOT_WIRED → a clear notice, no fake
  numbers).
  reads: useDeploymentIO (billing.status / billing.invoices / billing.tiers)
  note:  the first billing.status call provisions the user's $0 internal-tier
         Stripe customer + subscription (idempotent) — owner-authorised.
-->
<script lang="ts">
  import { useDeploymentIO } from "../../../../global/io/nodes/deployment/DeploymentIO.svelte";

  const node = useDeploymentIO();

  interface Subscription { status: string; currentPeriodEnd: number | null; itemCount: number; }
  interface Status { tierId: string; email: string | null; subscription: Subscription | null; invoiceCount: number; projectCount: number; }
  interface Invoice { id: string; number: string | null; status: string; amountDue: number; amountPaid: number; currency: string; createdAt: number; hostedInvoiceUrl: string | null; }

  let status = $state<Status | null>(null);
  let tierLabel = $state<string | null>(null);
  let invoices = $state<Invoice[]>([]);
  let loaded = $state(false);
  let err = $state<string | null>(null);
  let notConfigured = $state(false);

  function money(cents: number, currency: string): string {
    const cur = (currency || "usd").toUpperCase();
    try { return new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format((cents || 0) / 100); }
    catch { return `$${((cents || 0) / 100).toFixed(2)}`; }
  }
  function shortDate(unixSeconds: number): string {
    if (!unixSeconds) return "—";
    try { return new Date(unixSeconds * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return "—"; }
  }
  function coerceInvoice(raw: unknown): Invoice {
    const x = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
    return {
      id: typeof x["id"] === "string" ? x["id"] : "",
      number: typeof x["number"] === "string" ? x["number"] : null,
      status: typeof x["status"] === "string" ? x["status"] : "—",
      amountDue: typeof x["amountDue"] === "number" ? x["amountDue"] : 0,
      amountPaid: typeof x["amountPaid"] === "number" ? x["amountPaid"] : 0,
      currency: typeof x["currency"] === "string" ? x["currency"] : "usd",
      createdAt: typeof x["createdAt"] === "number" ? x["createdAt"] : 0,
      hostedInvoiceUrl: typeof x["hostedInvoiceUrl"] === "string" ? x["hostedInvoiceUrl"] : null,
    };
  }
  async function load(): Promise<void> {
    try {
      const st = (await node.dispatch("billing.status")) as Record<string, unknown>;
      const sub = (st?.["subscription"] ?? null) as Record<string, unknown> | null;
      status = {
        tierId: typeof st?.["tierId"] === "string" ? (st["tierId"] as string) : "—",
        email: typeof st?.["email"] === "string" ? (st["email"] as string) : null,
        subscription: sub ? {
          status: typeof sub["status"] === "string" ? sub["status"] : "—",
          currentPeriodEnd: typeof sub["currentPeriodEnd"] === "number" ? sub["currentPeriodEnd"] : null,
          itemCount: typeof sub["itemCount"] === "number" ? sub["itemCount"] : 0,
        } : null,
        invoiceCount: typeof st?.["invoiceCount"] === "number" ? (st["invoiceCount"] as number) : 0,
        projectCount: typeof st?.["projectCount"] === "number" ? (st["projectCount"] as number) : 0,
      };
      try {
        const t = (await node.dispatch("billing.tiers")) as { tiers?: Array<{ id: string; label: string }> };
        tierLabel = t?.tiers?.find((x) => x.id === status?.tierId)?.label ?? null;
      } catch { /* tier label is optional */ }
      try {
        const iv = (await node.dispatch("billing.invoices")) as { invoices?: unknown };
        invoices = Array.isArray(iv?.invoices) ? (iv.invoices as unknown[]).map(coerceInvoice) : [];
      } catch { /* invoices optional */ }
      err = null;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (/STRIPE_SECRET_KEY|not initialised|NOT_WIRED|billing unavailable/i.test(msg)) notConfigured = true;
      else err = msg;
    } finally { loaded = true; }
  }
  $effect(() => { void load(); });
</script>

<div class="cost">
  <header>
    <h1>Cost</h1>
    <p class="lede">Your billing account — every project rolls into one. Tier, subscription, and invoices, straight from Stripe.</p>
  </header>

  {#if !loaded}
    <p class="muted">Loading billing…</p>
  {:else if notConfigured}
    <div class="notice">
      <p class="notice-title">Billing isn't configured on this deployment</p>
      <p class="notice-body">No Stripe key is set on the brain, so there's nothing to bill against yet. This surface lights up — tier, subscription, invoices — the moment <code>STRIPE_SECRET_KEY</code> is configured. No placeholder numbers shown.</p>
    </div>
  {:else if err}
    <p class="err">{err}</p>
  {:else if status}
    <div class="cards">
      <section class="card tier">
        <span class="k">Plan</span>
        <span class="v">{tierLabel ?? status.tierId}</span>
        <span class="sub">{status.subscription ? `subscription ${status.subscription.status}` : "no active subscription"}{status.subscription?.currentPeriodEnd ? ` · renews ${shortDate(status.subscription.currentPeriodEnd)}` : ""}</span>
      </section>
      <section class="card">
        <span class="k">Projects</span>
        <span class="v">{status.projectCount}</span>
        <span class="sub">billed to this account</span>
      </section>
      <section class="card">
        <span class="k">Invoices</span>
        <span class="v">{status.invoiceCount}</span>
        <span class="sub">{invoices.length ? `${invoices.length} recent` : "lifetime"}</span>
      </section>
    </div>

    <section class="invoices">
      <h2>Recent invoices</h2>
      {#if invoices.length === 0}
        <p class="muted">No invoices yet — the internal tier is $0, so invoices appear as billing periods close.</p>
      {:else}
        <div class="table-wrap">
          <table>
            <thead>
              <tr><th>Invoice</th><th>Date</th><th>Status</th><th class="num">Amount</th><th></th></tr>
            </thead>
            <tbody>
              {#each invoices as inv (inv.id)}
                <tr>
                  <td class="mono">{inv.number ?? inv.id.slice(0, 14)}</td>
                  <td>{shortDate(inv.createdAt)}</td>
                  <td><span class="pill {inv.status}">{inv.status}</span></td>
                  <td class="num mono">{money(inv.amountDue, inv.currency)}</td>
                  <td class="link-cell">
                    {#if inv.hostedInvoiceUrl}
                      <a href={inv.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">view ↗</a>
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </section>
  {/if}
</div>

<style>
  .cost { display: flex; flex-direction: column; gap: var(--space-4); max-width: 1100px; font-family: var(--type-font); }
  header { display: flex; flex-direction: column; gap: var(--space-1); }
  h1 { margin: 0; font-size: 22px; font-weight: 600; letter-spacing: -0.02em; color: var(--text-primary); }
  .lede { margin: 0; font-size: 13.5px; color: var(--text-secondary); line-height: 1.55; max-width: 660px; }
  .muted { font-size: 13px; color: var(--text-tertiary); }
  .err { margin: 0; font-size: 12.5px; color: var(--error); font-family: var(--type-mono); }

  .notice { padding: var(--space-4); background: var(--warning-subtle, color-mix(in srgb, var(--warning) 10%, transparent)); border: 0.5px solid color-mix(in srgb, var(--warning) 30%, var(--border)); border-radius: var(--radius-lg, 14px); }
  .notice-title { margin: 0 0 var(--space-1); font-size: 14px; font-weight: 600; color: var(--text-primary); }
  .notice-body { margin: 0; font-size: 12.5px; color: var(--text-secondary); line-height: 1.55; }
  .notice code { font-family: var(--type-mono); font-size: 11.5px; }

  .cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--space-3); }
  @media (max-width: 720px) { .cards { grid-template-columns: 1fr; } }
  .card { display: flex; flex-direction: column; gap: 4px; padding: var(--space-4) var(--space-4) var(--space-3); background: var(--glass-fill); border: 0.5px solid var(--border); border-radius: var(--radius-lg, 14px); }
  .card.tier { border-color: color-mix(in srgb, var(--accent, #5b8cff) 28%, var(--border)); }
  .card .k { font-size: 11px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-tertiary); }
  .card .v { font-size: 22px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.1; color: var(--text-primary); text-transform: capitalize; }
  .card .sub { font-size: 11.5px; color: var(--text-tertiary); }

  .invoices { display: flex; flex-direction: column; gap: var(--space-3); }
  h2 { margin: 0; font-size: 15px; font-weight: 600; color: var(--text-primary); }
  .table-wrap { overflow-x: auto; border: 0.5px solid var(--border); border-radius: var(--radius-lg, 14px); background: var(--glass-fill); scrollbar-width: thin; }
  table { border-collapse: collapse; width: 100%; font-size: 12.5px; }
  th, td { padding: var(--space-2) var(--space-3); text-align: left; border-bottom: 0.5px solid var(--border-subtle); white-space: nowrap; }
  th { font-size: 10.5px; font-weight: 500; letter-spacing: 0.04em; text-transform: uppercase; color: var(--text-ghost); }
  tbody tr:last-child td { border-bottom: 0; }
  td { color: var(--text-secondary); }
  .mono { font-family: var(--type-mono); }
  .num { text-align: right; }
  .pill { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.04em; padding: 2px 8px; border-radius: var(--radius-pill); background: var(--bg-hover); color: var(--text-secondary); }
  .pill.paid { color: var(--success); background: color-mix(in srgb, var(--success) 14%, transparent); }
  .pill.open, .pill.draft { color: var(--warning); background: color-mix(in srgb, var(--warning) 14%, transparent); }
  .link-cell a { color: var(--accent, #5b8cff); text-decoration: none; font-size: 12px; }
  .link-cell a:hover { text-decoration: underline; }
</style>
